import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const AboutContainer = styled.div`
  min-height: 100vh;
  padding: 8rem 2rem 2rem;
  background: #0a0a0a;
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 0;
`;

const Title = styled.h2`
  font-size: 3rem;
  margin-bottom: 2rem;
  background: linear-gradient(45deg, #ffffff, #888888);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 2rem;

  & > *:nth-child(4),
  & > *:nth-child(5) {
    grid-column: span 1;
  }

  & > *:nth-child(4) {
    grid-column: span 3;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);

    & > *:nth-child(4) {
      grid-column: span 2;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;

    & > *:nth-child(4),
    & > *:nth-child(5) {
      grid-column: span 1;
    }
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 10px;
  backdrop-filter: blur(10px);
  height: 100%;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const SkillCategory = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryTitle = styled.h4`
  font-size: 1.2rem;
  color: #ffffff;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SkillList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
`;

const Skill = styled.span`
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const About = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const skillCategories = {
    Frameworks: [
      "React.JS",
      "Next.JS",
      "Node.JS",
      "Express.JS",
      "Flutter",
      "ASP.NET",
    ],
    "Programming Languages": [
      "JavaScript",
      "TypeScript",
      "C",
      "C++",
      "Python",
      "C#",
      "Java",
      "Dart",
    ],
    Database: ["MongoDB", "PostgreSQL", "Neo4j", "Redis"],
    DevOps: [
      "Azure",
      "Docker",
      "Kubernetes",
      "Terraform",
      "Ansible",
      "GitHub Actions",
      "Jenkins",
    ],
  };

  const certifications = [
    "Azure Administrator Associate",
    "GitHub Actions",
    "CompTIA Network+",
    "Google Cybersecurity Professional",
    "Stanford Machine Learning",
  ];

  useEffect(() => {
    // Animate title
    if (titleRef.current) {
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }

    // Animate cards
    cardRefs.current.forEach((card, index) => {
      if (card) {
        gsap.fromTo(
          card,
          { opacity: 0, x: -20 },
          {
            opacity: 1,
            x: 0,
            duration: 0.8,
            delay: index * 0.1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const addToCardRefs = (el: HTMLDivElement | null, index: number) => {
    cardRefs.current[index] = el;
  };

  return (
    <AboutContainer>
      <Section ref={sectionRef}>
        <Title ref={titleRef}>About Me</Title>
        <Content>
          <Card ref={(el) => addToCardRefs(el, 0)}>
            <h3>Background</h3>
            <p>
              I'm a Software Engineer with over 5 years of experience designing
              and building full-stack web, desktop, and mobile applications. I
              specialize in translating complex business requirements into
              scalable, efficient systems—combining clean, maintainable code
              with strong architectural design. With a background in both
              front-end and back-end development, I lead projects from initial
              concept and UI/UX prototyping all the way through testing, CI/CD
              pipelines, and production deployment.
            </p>
          </Card>
          <Card ref={(el) => addToCardRefs(el, 1)}>
            <h3>Experience</h3>
            <p>
              Over the years, I've worked on various projects ranging from
              enterprise applications to creative web experiences. My approach
              combines technical excellence with creative problem-solving to
              deliver exceptional results.
            </p>
          </Card>
          <Card ref={(el) => addToCardRefs(el, 2)}>
            <h3>Education</h3>
            <p>
              I am currently pursuing a Master's degree in Computer Science at
              the Georgia Institute of Technology, specializing in Computing
              Systems. Prior to this, I earned a Bachelor's degree in Computer
              Engineering from Minnesota State University, Mankato, where I
              focused on embedded systems and developed a strong foundation in
              both hardware and software integration.
            </p>
          </Card>
          <Card ref={(el) => addToCardRefs(el, 3)}>
            <h3>Skills</h3>
            {Object.entries(skillCategories).map(
              ([category, skills], categoryIndex) => (
                <SkillCategory key={category}>
                  <CategoryTitle>{category}</CategoryTitle>
                  <SkillList>
                    {skills.map((skill, index) => (
                      <Skill key={skill}>{skill}</Skill>
                    ))}
                  </SkillList>
                </SkillCategory>
              )
            )}
          </Card>
          <Card ref={(el) => addToCardRefs(el, 4)}>
            <h3>Certifications</h3>
            <SkillList>
              {certifications.map((certification, index) => (
                <Skill key={certification}>{certification}</Skill>
              ))}
            </SkillList>
          </Card>
        </Content>
      </Section>
    </AboutContainer>
  );
};

export default About;
